// =================================================================
// useAuth hook
//
// Tracks Firebase auth state across the app via a shared context.
// Components call `useAuth()` to read the current user (or null)
// and access login/logout helpers.
//
// Magic link flow:
//   1. User enters email on /admin/login OR /signup
//   2. We call sendSignInLinkToEmail() and store their email in
//      localStorage (Firebase needs it to verify when they return)
//   3. They click the link in their email
//   4. The link returns them to the original page (parameterized via
//      sendLoginLink's `returnTo` option)
//   5. That page detects the link, calls tryCompleteLinkSignIn(),
//      and they're authenticated
//
// Two callers today:
//   - /admin/login uses the default returnTo='/admin/login'
//     for the existing admin flow.
//   - /signup uses returnTo='/signup' for individual subscribers
//     (Phase 2 Brain Health individual self-serve path).
//
// Access control is layered (unchanged in Phase 2):
//   - Anyone can sign in (the magic-link request is no longer gated
//     to a specific email — anyone with a valid email can attempt)
//   - The super-admin (fpeyerl@quiltt.com) gets full admin access
//   - Community admins get scoped access via useAdminAccess
//   - Individual subscribers get access via the existence of a
//     /brain_health/users/{uid}/ record (see useIndividualUser)
//   - Signed-in users matching no admin role and no /users/ record
//     get redirected to /admin/no-access (from admin pages)
//     or to /signup (from individual pages, which will then offer
//     to set up an account)
// =================================================================

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from 'firebase/auth';
import { auth, SUPER_ADMIN_EMAIL } from './firebase.js';

const AuthContext = createContext(null);

const PENDING_EMAIL_KEY = 'brainHealth.pendingSignInEmail';

// Simple email format check — same one used in the database rules
// for contact_email validation. Keep these two regexes in sync.
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Build the actionCodeSettings for sendSignInLinkToEmail.
// The `returnTo` path determines which page Firebase redirects back
// to after the user clicks the link. Default is admin login for
// backward compatibility; signup passes its own.
function getActionCodeSettings(returnTo = '/admin/login') {
  return {
    url: `${window.location.origin}${returnTo}`,
    handleCodeInApp: true,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Send a magic sign-in link. `options.returnTo` defaults to
  // '/admin/login' for backward compatibility with existing admin
  // sign-in. Callers from /signup should pass returnTo: '/signup'.
  const sendLoginLink = useCallback(async (email, options = {}) => {
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalized)) {
      throw new Error('Please enter a valid email address.');
    }
    const { returnTo = '/admin/login' } = options;
    // We no longer pre-check against a hardcoded admin email.
    // Anyone with a valid email format can request a sign-in link.
    // Whether they have permissions to do anything once signed in
    // is determined by the database rules and the useAdminAccess /
    // useIndividualUser hooks.
    await sendSignInLinkToEmail(
      auth,
      normalized,
      getActionCodeSettings(returnTo)
    );
    localStorage.setItem(PENDING_EMAIL_KEY, normalized);
  }, []);

  const tryCompleteLinkSignIn = useCallback(async () => {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return { handled: false };
    }

    let email = localStorage.getItem(PENDING_EMAIL_KEY);
    if (!email) {
      email = window.prompt(
        'Please confirm the email address you used to sign in:'
      );
    }
    if (!email) {
      return { handled: true, error: 'Sign-in cancelled.' };
    }

    try {
      await signInWithEmailLink(auth, email, window.location.href);
      localStorage.removeItem(PENDING_EMAIL_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      return { handled: true };
    } catch (err) {
      return { handled: true, error: err.message };
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    localStorage.removeItem(PENDING_EMAIL_KEY);
  }, []);

  const value = {
    user,
    loading,
    isSignedIn: !!user,
    isSuperAdmin: !!user && user.email === SUPER_ADMIN_EMAIL,
    sendLoginLink,
    tryCompleteLinkSignIn,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
